//
// Copyright © 2024 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { Asset, IntlString, Plugin } from '@hcengineering/platform'
import { plugin } from '@hcengineering/platform'
import type { AttachedDoc, Class, CollectionSize, Doc, Mixin, Ref, Timestamp } from '@hcengineering/core'
import type { AnyComponent } from '@hcengineering/ui'
import type { Employee } from '@hcengineering/contact'
import type { Issue, Project } from '@hcengineering/tracker'

/**
 * @public
 */
export const timeTrackerId = 'time-tracker' as Plugin

/**
 * Активный таймер сотрудника. На одного сотрудника — не более одного.
 * @public
 */
export interface ActiveTimer extends Doc {
  employee: Ref<Employee>
  issue: Ref<Issue>
  project: Ref<Project>
  /** Unix-timestamp в миллисекундах — момент нажатия Start */
  startTime: Timestamp
}

/**
 * Завершённая запись учёта времени, прикреплённая к задаче (Issue).
 * @public
 */
export interface TimeEntry extends AttachedDoc {
  declare attachedTo: Ref<Issue>
  employee: Ref<Employee>
  project: Ref<Project>
  startTime: Timestamp
  endTime: Timestamp
  /** Длительность в секундах */
  duration: number
  description: string
  status: 'completed' | 'approved'
  /** true — запись создана вручную (не таймером) */
  isManual: boolean
}

/**
 * Mixin: добавляет счётчик записей времени к любому документу.
 * Применяется к tracker.class.Issue.
 * @public
 */
export interface Timeable extends Doc {
  timeEntries?: CollectionSize<TimeEntry>
}

export default plugin(timeTrackerId, {
  class: {
    ActiveTimer: '' as Ref<Class<ActiveTimer>>,
    TimeEntry: '' as Ref<Class<TimeEntry>>
  },
  mixin: {
    Timeable: '' as Ref<Mixin<Timeable>>
  },
  component: {
    TimerControl: '' as AnyComponent,
    ManualTimeEntryPopup: '' as AnyComponent,
    TaskTimeLog: '' as AnyComponent,
    IssueTimeSection: '' as AnyComponent,
    ReportsView: '' as AnyComponent
  },
  string: {
    TimeTracker: '' as IntlString,
    TimeEntry: '' as IntlString,
    TimeEntries: '' as IntlString,
    ActiveTimer: '' as IntlString,
    Start: '' as IntlString,
    Stop: '' as IntlString,
    LogTime: '' as IntlString,
    Duration: '' as IntlString,
    Description: '' as IntlString,
    Status: '' as IntlString,
    Approved: '' as IntlString,
    Completed: '' as IntlString,
    TotalTime: '' as IntlString,
    ReportByEmployee: '' as IntlString,
    ReportByProject: '' as IntlString,
    ReportByTask: '' as IntlString,
    ExportCSV: '' as IntlString,
    ExportExcel: '' as IntlString,
    ExportPDF: '' as IntlString
  },
  icon: {
    TimeTracker: '' as Asset,
    Start: '' as Asset,
    Stop: '' as Asset,
    LogTime: '' as Asset,
    Reports: '' as Asset
  }
})
